/**
 * Article validator runner.
 *
 * Usage:
 *   const result = validateArticle(article)
 *   result.hasBlockingErrors  // → gates publishing
 *   result.activeWarningCount // → toolbar badge
 *   result.errors             // → full list, pass to canvas + inspector
 *
 * To extend: add a new BlockValidator to BLOCK_VALIDATORS or a new
 * ArticleValidator to ARTICLE_VALIDATORS in rules.ts. No changes needed here.
 */

import type { WireArticleFull } from '../../../api/types'
import { ARTICLE_VALIDATORS, BLOCK_VALIDATORS } from './rules'
import type { ValidationContext, ValidationError, ValidationResult } from './types'

export type { ValidationError, ValidationResult, ValidationSeverity } from './types'

// ─── Runner ───────────────────────────────────────────────────────────────────

export function validateArticle(article: WireArticleFull): ValidationResult {
  // Build context once — shared across all validators
  const sourceList = article.blocks.find(b => b.type === 'source-list')
  const sourceIds = new Set<string>(
    sourceList?.type === 'source-list' ? sourceList.entries.map(e => e.id) : []
  )
  const ctx: ValidationContext = {
    article,
    sourceIds,
    blockIds: article.blocks.map(b => b.id),
  }

  const errors: ValidationError[] = []

  // Block-level pass
  for (const block of article.blocks) {
    for (const validator of BLOCK_VALIDATORS) {
      errors.push(...validator(block, ctx))
    }
  }

  // Article-level pass
  for (const validator of ARTICLE_VALIDATORS) {
    errors.push(...validator(article, ctx))
  }

  // Apply any previously acknowledged states stored on the article
  const acknowledgedCodes = new Set(
    (article.validationErrors ?? [])
      .filter(e => e.acknowledged)
      .map(e => `${e.code}:${'blockId' in e ? e.blockId : ''}`)
  )
  const merged = errors.map(e => ({
    ...e,
    acknowledged: acknowledgedCodes.has(`${e.code}:${'blockId' in e ? e.blockId : ''}`),
  }))

  const hasBlockingErrors = merged.some(e => e.severity === 'error')
  const activeWarningCount = merged.filter(
    e => e.severity === 'warning' && !e.acknowledged
  ).length

  return { errors: merged, hasBlockingErrors, activeWarningCount }
}

// ─── Acknowledgement helper ───────────────────────────────────────────────────

/**
 * Returns an updated article with the given error acknowledged.
 * Errors (severity === 'error') cannot be acknowledged.
 */
export function acknowledgeError(
  article: WireArticleFull,
  error: ValidationError
): WireArticleFull {
  if (error.severity === 'error') return article

  const existing = article.validationErrors ?? []
  const alreadyPresent = existing.some(
    e => e.code === error.code && 'blockId' in e === 'blockId' in error &&
      (!('blockId' in e) || (e as { blockId: string }).blockId === ('blockId' in error ? (error as { blockId: string }).blockId : ''))
  )

  if (alreadyPresent) {
    return {
      ...article,
      validationErrors: existing.map(e =>
        e.code === error.code ? { ...e, acknowledged: true } : e
      ),
    }
  }

  return {
    ...article,
    validationErrors: [...existing, { ...error, acknowledged: true }],
  }
}
